"use client"

import { useRef } from "react"
import { motion, useScroll, useSpring, useTransform } from "motion/react"
import { GeometricMaskedFrame } from "@/core/ui/geometric-masked-frame"

export default function PartnershipStory() {
  const containerRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  })

  // Smooth out scroll progress
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 40,
    damping: 15,
    restDelta: 0.001,
  })

  // Visual scaling and opacity transforms as user scrolls through story
  const scale = useTransform(smoothProgress, [0.2, 0.5, 0.8], [0.95, 1.02, 0.98])
  const opacity = useTransform(smoothProgress, [0.1, 0.3, 0.7, 0.9], [0.4, 1, 1, 0.4])

  const sections = [
    {
      num: "01",
      title: "Budget Symmetry",
      body: "Software shouldn't be a bottomless pit of hourly invoices. We provide absolute financial certainty through a single, fixed-rate partnership. Focus intensely on business value, unburdened by scope renegotiations and hourly clock-watching.",
    },
    {
      num: "02",
      title: "Seamless Integration",
      body: "We merge directly into your existing operational rhythms. Connected via Slack or Linear, we operate precisely as your internal founding engineer would—maintaining the highest levels of velocity without introducing external agency friction.",
    },
    {
      num: "03",
      title: "Radical Transparency",
      body: "The overarching goal is trust. From day one, you have unfettered access to the evolving repository, live architecture maps, and real-time sprint execution. Absolute clarity on every decision, every commit, and every milestone.",
    },
  ]

  return (
    <section ref={containerRef} className="relative bg-[#050507] pt-32 pb-48 border-t border-white/[0.06]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 relative items-start">
          {/* LEFT: The Sticky Geometric Masked Visual */}
          <div className="hidden lg:block relative h-full">
            <div className="sticky top-[18vh] w-full flex flex-col items-center justify-center">
              <motion.div style={{ scale, opacity }} className="w-full max-w-md">
                <div className="p-3 rounded-3xl bg-white/[0.02] border border-white/[0.08] backdrop-blur-xl shadow-[0_20px_60px_rgba(0,0,0,0.8)]">
                  <GeometricMaskedFrame
                    variant="quantum-bracket"
                    image="/images/products/agent-swarm-orchestrator.jpg"
                    alt="The Principal Partnership Engine"
                    glowColor="cyan"
                    badge={{
                      logo: "openai",
                      text: "The Principal Architecture Engine",
                      subtext: "100% Dedicated Execution",
                      statusColor: "#38bdf8",
                    }}
                  />
                </div>
              </motion.div>

              <div className="mt-8 text-center">
                <p className="text-[11px] text-slate-500 uppercase tracking-[0.25em] font-mono">
                  Autonomous Core // Zero Agency Friction
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT: Elegant Story Content */}
          <div className="pt-[5vh] pb-[10vh] space-y-40 lg:pl-6">
            {sections.map((sec, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ margin: "-15% 0px -15% 0px" }}
                className="group"
              >
                <div className="mb-6 flex items-center gap-4">
                  <span className="text-4xl sm:text-5xl font-mono font-bold text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 to-sky-600 opacity-80">
                    {sec.num}
                  </span>
                  <div className="h-[1px] flex-1 bg-gradient-to-r from-cyan-500/30 via-white/10 to-transparent" />
                </div>

                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-100 mb-6 group-hover:text-cyan-300 transition-colors duration-300">
                  {sec.title}
                </h3>

                <p className="text-base sm:text-lg text-slate-400 leading-relaxed font-light">
                  {sec.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
